import { zipSync, strToU8 } from 'fflate'
import { VaultData, Folder, VaultItem } from '../config'

// Build folder path from folder ID
function getFolderPath(folders: Folder[], folderId: string | null): string {
  if (!folderId) return ''

  const folder = folders.find((f) => f.id === folderId)
  if (!folder) return ''

  const parentPath = getFolderPath(folders, folder.parentId)
  return parentPath
    ? `${parentPath}/${sanitizeFilename(folder.name)}`
    : sanitizeFilename(folder.name)
}

// Sanitize filename for safe filesystem usage
function sanitizeFilename(name: string): string {
  return name
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .substring(0, 100)
}

// Make filename unique within a directory
function makeUniqueFilename(
  existingNames: Set<string>,
  baseName: string,
  ext: string
): string {
  let name = `${baseName}${ext}`
  let counter = 1

  while (existingNames.has(name.toLowerCase())) {
    name = `${baseName} (${counter})${ext}`
    counter++
  }

  existingNames.add(name.toLowerCase())
  return name
}

// Generate markdown content for a bookmark
function bookmarkToMarkdown(
  item: VaultItem,
  tags: { id: string; name: string }[]
): string {
  const itemTags = tags.filter((t) => item.tagIds.includes(t.id))
  const tagLine =
    itemTags.length > 0
      ? `Tags: ${itemTags.map((t) => `#${t.name}`).join(' ')}\n\n`
      : ''

  let content = `# ${item.title}\n\n`
  content += `**URL:** [${item.content}](${item.content})\n\n`
  content += tagLine

  if (item.description) {
    content += `## Description\n\n${item.description}\n\n`
  }

  content += `---\n`
  content += `Created: ${new Date(item.createdAt).toLocaleString()}\n`
  content += `Updated: ${new Date(item.updatedAt).toLocaleString()}\n`

  return content
}

// Generate markdown content for a note
function noteToMarkdown(
  item: VaultItem,
  tags: { id: string; name: string }[]
): string {
  const itemTags = tags.filter((t) => item.tagIds.includes(t.id))
  const tagLine =
    itemTags.length > 0
      ? `Tags: ${itemTags.map((t) => `#${t.name}`).join(' ')}\n\n`
      : ''

  let content = `# ${item.title}\n\n`
  content += tagLine
  content += item.content
  content += `\n\n---\n`
  content += `Created: ${new Date(item.createdAt).toLocaleString()}\n`
  content += `Updated: ${new Date(item.updatedAt).toLocaleString()}\n`

  return content
}

// Export vault data as a zip file
export function exportVaultAsZip(vaultId: string, data: VaultData): Uint8Array {
  const files: { [path: string]: Uint8Array } = {}
  const bookmarkNames: Map<string, Set<string>> = new Map()
  const noteNames: Map<string, Set<string>> = new Map()

  // Process bookmarks
  const bookmarks = data.items.filter((item) => item.type === 'bookmark')
  for (const item of bookmarks) {
    const folderPath = getFolderPath(data.folders, item.folderId)
    const basePath = folderPath ? `bookmarks/${folderPath}` : 'bookmarks'

    if (!bookmarkNames.has(basePath)) {
      bookmarkNames.set(basePath, new Set())
    }

    const filename = makeUniqueFilename(
      bookmarkNames.get(basePath)!,
      sanitizeFilename(item.title),
      '.md'
    )

    const fullPath = `${basePath}/${filename}`
    const content = bookmarkToMarkdown(item, data.tags)
    files[fullPath] = strToU8(content)
  }

  // Process notes
  const notes = data.items.filter((item) => item.type === 'note')
  for (const item of notes) {
    const folderPath = getFolderPath(data.folders, item.folderId)
    const basePath = folderPath ? `notes/${folderPath}` : 'notes'

    if (!noteNames.has(basePath)) {
      noteNames.set(basePath, new Set())
    }

    const filename = makeUniqueFilename(
      noteNames.get(basePath)!,
      sanitizeFilename(item.title),
      '.md'
    )

    const fullPath = `${basePath}/${filename}`
    const content = noteToMarkdown(item, data.tags)
    files[fullPath] = strToU8(content)
  }

  // Create metadata.json
  const metadata = {
    vaultId,
    exportedAt: new Date().toISOString(),
    folders: data.folders.map((f) => ({
      id: f.id,
      name: f.name,
      parentId: f.parentId,
    })),
    tags: data.tags.map((t) => ({
      id: t.id,
      name: t.name,
      color: t.color,
    })),
    stats: {
      totalItems: data.items.length,
      bookmarks: bookmarks.length,
      notes: notes.length,
      folders: data.folders.length,
      tags: data.tags.length,
    },
  }

  files['metadata.json'] = strToU8(JSON.stringify(metadata, null, 2))

  // Create zip
  return zipSync(files, { level: 6 })
}

// Trigger download of the zip file
export function downloadZip(vaultId: string, zipData: Uint8Array): void {
  const blob = new Blob([zipData], { type: 'application/zip' })
  const url = URL.createObjectURL(blob)

  const date = new Date().toISOString().split('T')[0]
  const filename = `vault-export-${vaultId}-${date}.zip`

  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
